/**
 * V112-3-10: one answer row of the scope screen, and its state chips.
 *
 * States (ADR-0099 §5, owner D3–D5):
 *   inherit — "Inherited from workspace": the workspace value, read only, with "Change for this system".
 *   set     — "Set for this system · Name, 21 Oct" (or "· before Oct 2026" with "Confirm as is"), the editor,
 *             "Workspace: …" and "Reset to workspace" (or "Same as workspace · Reset").
 *   open    — "Pick this system's sector" (several workspace industries, nothing picked).
 *   waiting — "Waiting on sector: Healthcare (kept)" (D5).
 *   only    — a system-only answer: who/when stamp or "Not answered".
 *   plain   — no state chrome (workspace defaults; the caller passes its own head).
 * Unsaved edits add the dashed "Changed" chip (and the row's dashed rail).
 */
import { useId, useState, type ReactNode } from 'react';
import type { ScopeAnswerMeta } from '@aigovops/core';
import { shortDay, shortName } from './scope-options';

export type ChipKind = 'inherit' | 'set' | 'todo' | 'changed' | 'same' | 'quiet' | 'check';

const GLYPH: Record<ChipKind, ReactNode> = {
  inherit: <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true"><path d="M6.5 9.5l3-3M7.2 4.3l.9-.9a2.6 2.6 0 013.7 3.7l-.9.9M8.8 11.7l-.9.9a2.6 2.6 0 01-3.7-3.7l.9-.9" /></svg>,
  set: <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M6 .8 11.2 6 6 11.2.8 6z" fill="currentColor" /></svg>,
  quiet: <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M6 1.6 10.4 6 6 10.4 1.6 6z" fill="none" stroke="currentColor" strokeWidth="1.5" /></svg>,
  todo: <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><circle cx="6" cy="6" r="4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeDasharray="2.2 1.6" /></svg>,
  changed: <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><circle cx="6" cy="6" r="3.5" fill="none" stroke="currentColor" strokeWidth="1.8" /></svg>,
  same: <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 8.5l3 3 7-7" /></svg>,
  check: <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 8.5l3 3 7-7" /></svg>,
};
export const Caret = () => <svg className="sc-caret" width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4.5 2.5 8 6l-3.5 3.5" /></svg>;

/** A state chip. `parts` = [state, who]: two no-wrap halves so a wrap falls at the "·", never inside a name. */
export function ScopeChip({ kind, children, parts, id }: { kind: ChipKind; children?: ReactNode; parts?: [string, string]; id?: string }) {
  return (
    <span className="sc-chip" data-kind={kind} id={id}>
      {GLYPH[kind]}
      {parts ? (
        <>
          <span className="sc-nw">{parts[0]}{parts[1] ? <><span aria-hidden="true">&nbsp;·</span><span className="sr-only">,</span></> : null}</span>
          {parts[1] ? <span className="sc-nw">{parts[1]}</span> : null}
        </>
      ) : children}
    </span>
  );
}

/** The who/when of a set answer; with a reason, a toggle that shows it (D4). */
export function StampChip({ state, meta, kind = 'set', id }: { state: string; meta: ScopeAnswerMeta | null | undefined; kind?: ChipKind; id?: string }) {
  const [open, setOpen] = useState(false);
  const whyId = `${useId().replace(/[^a-zA-Z0-9_-]/g, '')}-why`;
  const who = meta ? `${shortName(meta.by_name)}, ${shortDay(meta.at)}` : 'before Oct 2026';
  const chip = <ScopeChip kind={kind} id={id} parts={[state, who]} />;
  if (!meta?.reason) return chip;
  return (
    <span style={{ display: 'grid', gap: 'var(--space-1)' }}>
      <button type="button" className="sc-prov" aria-expanded={open} aria-controls={whyId} onClick={() => setOpen((o) => !o)}>
        {chip}<span className="sr-only">, show reason</span>
      </button>
      <p className="sc-why" id={whyId} hidden={!open}>“{meta.reason}”</p>
    </span>
  );
}

export type RowState = 'inherit' | 'set' | 'open' | 'waiting' | 'only' | 'plain';

export interface ScopeAnswerRowProps {
  /** Id stem: `a-<answer>` on the row, `h-<answer>` on its heading (focus targets). */
  answer: string;
  name: string;
  hint?: string;
  state: RowState;
  dirty?: boolean;
  canEdit?: boolean;
  /** set / only: the answer's stamp (null = carried over from before Oct 2026). */
  meta?: ScopeAnswerMeta | null;
  /** only: whether the system-only answer is answered. */
  answered?: boolean;
  /** A "Confirm as is" is pending on this row. */
  pendingAsIs?: boolean;
  /** inherit: the workspace value items. set: the comparison text. */
  workspaceItems?: string[];
  workspaceText?: string;
  sameAsWorkspace?: boolean;
  /** waiting: the sector the system kept. */
  waitingOn?: string;
  onChange?: () => void;
  onReset?: () => void;
  onConfirmAsIs?: () => void;
  onUndoAsIs?: () => void;
  /** The answer heading's level (h3 under a section h2; h4 where the section heading is an h3). */
  headingLevel?: 'h3' | 'h4';
  /** Extra head content (workspace indicator, notes). */
  headExtra?: ReactNode;
  /** Shown under an inherited value when the workspace hasn't answered (no dead end). */
  emptyAction?: ReactNode;
  children?: ReactNode;
}

export function ScopeAnswerRow(p: ScopeAnswerRowProps) {
  const { answer: k, state, dirty = false, canEdit = false } = p;
  const changed = <ScopeChip kind="changed">Changed</ScopeChip>;
  // A pending "Confirm as is" is its own state: the value doesn't change, the stamp does (with Undo).
  const asIsChip = (
    <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--space-1)' }}>
      <ScopeChip kind="check">Will be confirmed as is</ScopeChip>
      {p.onUndoAsIs ? <button type="button" className="sc-link" aria-describedby={`h-${k}`} onClick={p.onUndoAsIs}>Undo</button> : null}
    </span>
  );
  let head: ReactNode = null;
  let body: ReactNode = p.children;

  if (state === 'inherit') {
    head = (
      <>
        <ScopeChip kind="inherit" id={`st-${k}`}>Inherited from workspace</ScopeChip>
        {dirty ? changed : null}
        {canEdit && p.onChange ? (
          <button type="button" className="sc-link" aria-describedby={`h-${k} st-${k} val-${k}`} onClick={p.onChange}>Change for this system</button>
        ) : null}
      </>
    );
    const items = p.workspaceItems ?? [];
    body = (
      <>
        <div id={`val-${k}`}>
          <span className="sr-only">Workspace value: </span>
          <ul className="sc-value" role="list">{items.length ? items.map((x) => <li key={x}>{x}</li>) : <li>Not answered</li>}</ul>
        </div>
        {!items.length ? p.emptyAction : null}
        {p.children}
      </>
    );
  } else if (state === 'set') {
    head = dirty
      ? <><ScopeChip kind="set" id={`st-${k}`}>Set for this system</ScopeChip>{changed}</>
      : p.pendingAsIs
        ? <><StampChip state="Set for this system" meta={p.meta} id={`st-${k}`} />{asIsChip}</>
        : (
          <>
            <StampChip state={p.meta?.waiting ? 'Kept for this system' : 'Set for this system'} meta={p.meta} id={`st-${k}`} />
            {!p.meta && canEdit && p.onConfirmAsIs ? <button type="button" className="sc-link" aria-describedby={`h-${k}`} onClick={p.onConfirmAsIs}>Confirm as is</button> : null}
          </>
        );
    body = (
      <>
        {p.children}
        <div className="sc-compare">
          {p.sameAsWorkspace
            ? <><ScopeChip kind="same" id={`cmp-${k}`}>Same as workspace</ScopeChip>{canEdit && p.onReset ? <><span aria-hidden="true">·</span><button type="button" className="sc-link" aria-describedby={`h-${k} cmp-${k}`} onClick={p.onReset}>Reset</button></> : null}</>
            : <><span id={`cmp-${k}`}>{p.workspaceText}</span>{canEdit && p.onReset ? <button type="button" className="sc-link" aria-describedby={`h-${k} cmp-${k}`} onClick={p.onReset}>Reset to workspace</button> : null}</>}
        </div>
      </>
    );
  } else if (state === 'open') {
    head = <><ScopeChip kind="todo" id={`st-${k}`}>Pick this system’s sector</ScopeChip>{dirty ? changed : null}</>;
  } else if (state === 'waiting') {
    head = <><ScopeChip kind="todo" id={`st-${k}`}>{`Waiting on sector: ${p.waitingOn ?? ''} (kept)`}</ScopeChip>{dirty ? changed : null}</>;
  } else if (state === 'only') {
    head = !p.answered
      ? <><ScopeChip kind="todo" id={`st-${k}`}>Not answered</ScopeChip>{dirty ? changed : null}</>
      : dirty
        ? changed
        : p.pendingAsIs
          ? <><StampChip state="Set for this system" meta={p.meta} kind="quiet" id={`st-${k}`} />{asIsChip}</>
          : (
            <>
              <StampChip state="Set for this system" meta={p.meta} kind="quiet" id={`st-${k}`} />
              {!p.meta && canEdit && p.onConfirmAsIs ? <button type="button" className="sc-link" aria-describedby={`h-${k}`} onClick={p.onConfirmAsIs}>Confirm as is</button> : null}
            </>
          );
  } else if (dirty) {
    head = changed;
  }

  const H = p.headingLevel ?? 'h3';
  return (
    <section className="sc-answer" id={`a-${k}`} data-state={state} data-dirty={dirty}>
      <div className="sc-a-head">
        <H className="sc-a-name" id={`h-${k}`} tabIndex={-1}>{p.name}</H>
        {p.hint ? <p className="sc-hint" id={`d-${k}`}>{p.hint}</p> : null}
        {head}
        {p.headExtra}
      </div>
      <div className="sc-a-body">{body}</div>
    </section>
  );
}
